/**
 * O que o brilho do "Atlas" mostra (spec 011, pedido do Leopoldo de
 * 03/10/2026: "uma animação que demonstre que o atlas está ativo, semelhante
 * à Siri do iPhone"). Quem desenha é o `AtlasGlow` (Shell); aqui só o estado:
 *
 *   ouvindo → "Atlas" sozinho, esperando o pedido (some quando a janela fecha)
 *   pedido  → entendeu o pedido e mandou pra tela (mostra o quê)
 *   feito / recusa → quem atendeu disse se deu certo; some logo depois
 *
 * Esperando o pedido (e no "não entendi"), mostra também os pedidos que valem
 * nesta tela (`dicas` — pedido do Leopoldo de 05/10/2026: "apresente as
 * opções de comandos na tela pra ficar mais fácil de eu lembrar").
 *
 * Só o "Atlas" acende: o "Jarvis" (ditado) tem a coluna do ao vivo.
 */

import { useSyncExternalStore } from 'react';
import type { PrintTarget } from '../live';
import type { SystemCommand } from './matcher';
import { comandosComQuemAtenda } from './commands';

export type AtlasFase = 'ouvindo' | 'pedido' | 'feito' | 'recusa';

export interface AtlasEstado {
  fase: AtlasFase;
  rotulo: string;
  /** Cada pedido tem o seu: resposta atrasada de um pedido velho não mexe no novo. */
  seq: number;
  /** Os pedidos que valem nesta tela, pra lembrar (só esperando o pedido e no "não entendi"). */
  dicas?: SystemCommand[];
}

/** Quanto o resultado fica na tela antes de sumir. */
export const ATLAS_FEITO_MS = 1400;
export const ATLAS_RECUSA_MS = 2000;
/** "Não entendi" mostra a lista: fica o bastante pra ler. */
export const ATLAS_NAO_ENTENDI_MS = 4000;
/** Pedido sem resposta (quem atende travou): o brilho não fica aceso pra sempre. */
const ATLAS_PEDIDO_MAX_MS = 10_000;

let atual: AtlasEstado | null = null;
let seq = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
const ouvintes = new Set<() => void>();

function definir(estado: AtlasEstado | null, duracaoMs?: number): void {
  if (timer) clearTimeout(timer);
  timer = null;
  atual = estado;
  ouvintes.forEach((f) => f());
  if (estado && duracaoMs) {
    timer = setTimeout(() => {
      timer = null;
      atual = null;
      ouvintes.forEach((f) => f());
    }, duracaoMs);
  }
}

/**
 * O que lembrar: quem atende nesta tela (`useComandoVoz` ativo). Na agenda,
 * "abre a agenda" não faz sentido mostrar (funciona, mas já está lá).
 */
function dicasDaTela(): SystemCommand[] {
  const naAgenda = typeof window !== 'undefined' && window.location.pathname === '/agenda';
  return comandosComQuemAtenda().filter((c) => !(naAgenda && c === 'agenda'));
}

/** "Atlas" sozinho: acende, mostra os pedidos e espera por `ms`. */
export function atlasOuvindo(ms: number): void {
  definir({ fase: 'ouvindo', rotulo: 'ouvindo…', seq: ++seq, dicas: dicasDaTela() }, ms);
}

/** Entendeu o pedido. Devolve o número dele, pra `atlasTerminou`. */
export function atlasPedido(rotulo: string): number {
  definir({ fase: 'pedido', rotulo, seq: ++seq }, ATLAS_PEDIDO_MAX_MS);
  return seq;
}

/** Quem atendeu respondeu. Pedido que já não é o da tela não mexe em nada. */
export function atlasTerminou(pedido: number, ok: boolean): void {
  if (!atual || atual.seq !== pedido) return;
  definir({ ...atual, fase: ok ? 'feito' : 'recusa' }, ok ? ATLAS_FEITO_MS : ATLAS_RECUSA_MS);
}

/** "Atlas" sozinho e a frase seguinte não era pedido: mostra de novo o que vale. */
export function atlasNaoEntendeu(): void {
  definir({ fase: 'recusa', rotulo: 'Não entendi o pedido', seq: ++seq, dicas: dicasDaTela() }, ATLAS_NAO_ENTENDI_MS);
}

/** Apaga o "ouvindo" (a escuta parou, ou chamaram o "Jarvis" no meio). */
export function atlasCancelar(): void {
  if (atual?.fase === 'ouvindo') definir(null);
}

/** Apaga o que estiver aceso, seja o que for (testes). */
export function atlasLimpar(): void {
  definir(null);
}

export function getAtlasEstado(): AtlasEstado | null {
  return atual;
}

function assinar(f: () => void): () => void {
  ouvintes.add(f);
  return () => ouvintes.delete(f);
}

export function useAtlasEstado(): AtlasEstado | null {
  return useSyncExternalStore(assinar, getAtlasEstado, () => null);
}

const ALVO: Record<PrintTarget, string> = {
  tudo: 'tudo',
  receitaOculos: 'os óculos',
  receitaMedicamentos: 'a receita',
  solicitacaoExames: 'os exames',
  solicitacaoCirurgia: 'a cirurgia',
  atestadoLaudo: 'o atestado/laudo',
};

/** O pedido em português, pra o médico ver de relance o que o Atlas entendeu. */
export function rotuloDoPedido(comando: SystemCommand, alvos?: PrintTarget[]): string {
  switch (comando) {
    case 'proximo':
      return 'Abrir o próximo paciente';
    case 'mandarEntrar':
      return 'Mandar entrar';
    case 'agenda':
      return 'Abrir a agenda';
    case 'salvar':
      return 'Registrar no prontuário';
    case 'pausar':
      return 'Pausar o ditado';
    case 'retomar':
      return 'Voltar a ouvir';
    case 'parar':
      return 'Desligar o ditado';
    case 'imprimir':
      return `Imprimir ${(alvos?.length ? alvos : (['tudo'] as PrintTarget[])).map((a) => ALVO[a]).join(' e ')}`;
  }
}

/**
 * A lista que aparece quando o Atlas espera o pedido: a frase mais curta que
 * o `matcher.ts` aceita (e que está no `PEDIDOS_ATLAS_VOSK`, pro motor
 * próprio) e o que ela faz. O teste `atlasGlow` confere que toda `fala` vira
 * o comando dela.
 */
export const DICAS_ATLAS: Record<SystemCommand, { fala: string; faz: string }> = {
  proximo: { fala: 'abre o próximo', faz: 'ficha do próximo paciente' },
  mandarEntrar: { fala: 'manda entrar', faz: 'avisa a recepção' },
  agenda: { fala: 'abre a agenda', faz: 'volta pra agenda' },
  imprimir: { fala: 'imprime tudo', faz: 'ou: os óculos · a receita · os exames · a cirurgia · o atestado' },
  salvar: { fala: 'salve', faz: 'registra no prontuário' },
  pausar: { fala: 'pause', faz: 'pausa o ao vivo' },
  retomar: { fala: 'continua', faz: 'volta a ouvir' },
  parar: { fala: 'pare', faz: 'desliga o ao vivo' },
};
